<template>
  <div class="admin-transactions-page">
    <div class="page-header">
      <h1 class="page-title">
        {{ t('admin.transactions.title') }}
      </h1>
      <p class="page-subtitle">
        {{ t('admin.transactions.subtitle') }}
      </p>
    </div>

    <!-- Filters -->
    <div class="filters-section shadow-card">
      <div class="filters-grid">
        <div class="filter-group">
          <label class="filter-label">{{ t('admin.transactions.search') }}</label>
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="t('admin.transactions.search_placeholder')"
            class="filter-input input-admin-on-focus"
            @input="handleSearch"
          >
        </div>

        <div class="filter-group">
          <label class="filter-label">{{ t('admin.transactions.type') }}</label>
          <select
            v-model="typeFilter"
            class="filter-select input-admin-on-focus"
            @change="loadTransactions"
          >
            <option class="bg-adminBox-option text-adminBox-text" value="">
              {{ t('admin.transactions.type_all') }}
            </option>
            <option class="bg-adminBox-option text-adminBox-text" value="PURCHASE">
              {{ t('admin.transactions.type_purchase') }}
            </option>
            <option class="bg-adminBox-option text-adminBox-text" value="REFUND">
              {{ t('admin.transactions.type_refund') }}
            </option>
            <option class="bg-adminBox-option text-adminBox-text" value="TRANSFER">
              {{ t('admin.transactions.type_transfer') }}
            </option>
            <option class="bg-adminBox-option text-adminBox-text" value="WITHDRAWAL">
              {{ t('admin.transactions.type_withdrawal') }}
            </option>
            <option class="bg-adminBox-option text-adminBox-text" value="DEPOSIT">
              {{ t('admin.transactions.type_deposit') }}
            </option>
          </select>
        </div>

        <div class="filter-group">
          <label class="filter-label">{{ t('admin.transactions.status') }}</label>
          <select
            v-model="statusFilter"
            class="filter-select input-admin-on-focus"
            @change="loadTransactions"
          >
            <option class="bg-adminBox-option text-adminBox-text" value="">
              {{ t('admin.transactions.status_all') }}
            </option>
            <option class="bg-adminBox-option text-adminBox-text" value="PENDING">
              {{ t('admin.transactions.status_pending') }}
            </option>
            <option class="bg-adminBox-option text-adminBox-text" value="COMPLETED">
              {{ t('admin.transactions.status_completed') }}
            </option>
            <option class="bg-adminBox-option text-adminBox-text" value="FAILED">
              {{ t('admin.transactions.status_failed') }}
            </option>
            <option class="bg-adminBox-option text-adminBox-text" value="CANCELLED">
              {{ t('admin.transactions.status_cancelled') }}
            </option>
            <option class="bg-adminBox-option text-adminBox-text" value="REFUNDED">
              {{ t('admin.transactions.status_refunded') }}
            </option>
          </select>
        </div>

        <div class="filter-group">
          <label class="filter-label">{{ t('admin.transactions.payment_method') }}</label>
          <select
            v-model="paymentMethodFilter"
            class="filter-select input-admin-on-focus"
            @change="loadTransactions"
          >
            <option class="bg-adminBox-option text-adminBox-text" value="">
              {{ t('admin.transactions.type_all') }}
            </option>
            <option class="bg-adminBox-option text-adminBox-text" value="VNPAY">
              {{ t('admin.transactions.method_vnpay') }}
            </option>
            <option class="bg-adminBox-option text-adminBox-text" value="BANK_TRANSFER">
              {{ t('admin.transactions.method_bank_transfer') }}
            </option>
            <option class="bg-adminBox-option text-adminBox-text" value="CRYPTO">
              {{ t('admin.transactions.method_crypto') }}
            </option>
            <option class="bg-adminBox-option text-adminBox-text" value="CASH">
              {{ t('admin.transactions.method_cash') }}
            </option>
          </select>
        </div>

        <div class="filter-group">
          <label class="filter-label">{{ t('admin.transactions.sort_by') }}</label>
          <select
            v-model="sortBy"
            class="filter-select input-admin-on-focus"
            @change="loadTransactions"
          >
            <option class="bg-adminBox-option text-adminBox-text" value="createdAt">
              {{ t('admin.transactions.sort_created') }}
            </option>
            <option class="bg-adminBox-option text-adminBox-text" value="amount">
              {{ t('admin.transactions.sort_amount') }}
            </option>
            <option class="bg-adminBox-option text-adminBox-text" value="status">
              {{ t('admin.transactions.sort_status') }}
            </option>
            <option class="bg-adminBox-option text-adminBox-text" value="type">
              {{ t('admin.transactions.sort_type') }}
            </option>
          </select>
        </div>

        <div class="filter-group">
          <label class="filter-label">{{ t('admin.transactions.order') }}</label>
          <select
            v-model="sortOrder"
            class="filter-select input-admin-on-focus"
            @change="loadTransactions"
          >
            <option class="bg-adminBox-option text-adminBox-text" value="desc">
              {{ t('admin.transactions.order_desc') }}
            </option>
            <option class="bg-adminBox-option text-adminBox-text" value="asc">
              {{ t('admin.transactions.order_asc') }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <!-- Transactions Table -->
    <div class="table-section shadow-card">
      <div class="table-header">
        <h3 class="table-title">
          {{ t('admin.transactions.table_title') }}
        </h3>
        <div class="table-actions">
          <button
            class="btn btn-admin-secondary"
            @click="exportTransactions"
          >
            <i class="fas fa-download mr-2" />
            {{ t('admin.transactions.export_excel') }}
          </button>
        </div>
      </div>

      <div class="table-container scrollbar-glass">
        <table class="transactions-table">
          <thead>
            <tr>
              <th>{{ t('admin.transactions.table_transaction') }}</th>
              <th>{{ t('admin.transactions.table_user') }}</th>
              <th>{{ t('admin.transactions.table_event') }}</th>
              <th>{{ t('admin.transactions.table_type') }}</th>
              <th>{{ t('admin.transactions.table_status') }}</th>
              <th>{{ t('admin.transactions.table_amount') }}</th>
              <th>{{ t('admin.transactions.table_created_at') }}</th>
              <th>{{ t('admin.transactions.table_actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="isTransactionsLoading">
              <td
                colspan="8"
                class="loading-cell"
              >
                <div class="loading-spinner" />
                <span>{{ t('admin.transactions.loading') }}</span>
              </td>
            </tr>

            <tr v-else-if="!transactions || transactions.length === 0">
              <td
                colspan="8"
                class="empty-cell"
              >
                <i class="fas fa-receipt text-4xl text-gray-400" />
                <p>{{ t('admin.transactions.no_transactions') }}</p>
              </td>
            </tr>

            <tr
              v-for="transaction in transactions"
              v-else
              :key="transaction.id"
              class="transaction-row"
            >
              <td class="transaction-cell">
                <div class="transaction-info">
                  <div class="transaction-id">
                    <span class="id-label">{{ t('admin.transactions.id_label') }}:</span>
                    <span class="id-value">{{ transaction.id.substring(0, 8) }}...</span>
                  </div>
                  <div class="transaction-method">
                    <span class="method-badge">{{ transaction.paymentMethod ? getPaymentMethodText(transaction.paymentMethod) : t('admin.transactions.unknown') }}</span>
                  </div>
                </div>
              </td>

              <td class="user-cell">
                <div class="user-info">
                  <div class="user-avatar">
                    <img
                      v-if="transaction.user?.avatar"
                      :src="transaction.user.avatar"
                      :alt="transaction.user?.name"
                      class="avatar-img"
                    >
                    <div v-else class="avatar-letter">
                      {{ transaction.user?.name?.charAt(0) || transaction.user?.email?.charAt(0) || 'M' }}
                    </div>
                  </div>
                  <div class="user-details">
                    <h4 class="user-name">
                      {{ transaction.user?.name }}
                    </h4>
                    <p class="user-email">
                      {{ transaction.user?.email }}
                    </p>
                  </div>
                </div>
              </td>

              <td class="event-cell">
                <div
                  v-if="transaction.event"
                  class="event-info"
                >
                  <h4 class="event-title">
                    {{ transaction.event.title }}
                  </h4>
                  <p class="event-id">
                    {{ t('admin.transactions.id_label') }}: {{ transaction.event.id?.substring(0, 8) }}...
                  </p>
                </div>
                <div
                  v-else
                >
                  <span class="no-event-text">{{ t('admin.transactions.no_event') }}</span>
                </div>
              </td>

              <td class="type-cell">
                <span
                  class="type-badge"
                  :class="`type-${transaction.type.toLowerCase()}`"
                >
                  {{ getTypeText(transaction.type) }}
                </span>
              </td>

              <td class="status-cell">
                <span
                  class="status-badge"
                  :class="`status-${transaction.status.toLowerCase()}`"
                >
                  {{ getStatusText(transaction.status) }}
                </span>
              </td>

              <td class="amount-cell">
                <div class="amount-info">
                  <span class="amount-value">{{ formatPrice(transaction.amount) }}</span>
                  <span class="amount-currency">{{ transaction.currency }}</span>
                </div>
              </td>

              <td class="date-cell">
                <div class="date-info">
                  <span class="date-text">{{ formatDate(transaction.createdAt) }}</span>
                  <span class="time-text">{{ formatTime(transaction.createdAt) }}</span>
                </div>
              </td>

              <td>
                <div class="action-buttons">
                  <button
                    class="action-btn btn-view"
                    :title="t('admin.transactions.view_details')"
                    @click="viewTransaction(transaction)"
                  >
                    <i class="fas fa-eye" />
                  </button>

                  <div class="dropdown relative">
                    <button
                      class="action-btn btn-more"
                      :title="t('admin.transactions.more_actions')"
                      @click.stop="toggleMenu(transaction.id)"
                    >
                      <i class="fas fa-ellipsis-v" />
                    </button>
                    <div
                      v-show="openMenuTransactionId === transaction.id"
                      class="dropdown-menu"
                      @click.stop
                      @mouseenter="keepMenuOpen(transaction.id)"
                      @mouseleave="scheduleMenuClose(transaction.id)"
                    >
                      <button
                        v-if="transaction.status === 'PENDING'"
                        class="dropdown-item approve-transaction"
                        @click="approveTransaction(transaction.id)"
                      >
                        <i class="fas fa-check mr-2" />
                        {{ t('admin.transactions.action_approve') }}
                      </button>
                      <button
                        v-if="transaction.status === 'PENDING'"
                        class="dropdown-item reject-transaction"
                        @click="rejectTransaction(transaction.id)"
                      >
                        <i class="fas fa-times mr-2" />
                        {{ t('admin.transactions.action_reject') }}
                      </button>
                      <button
                        v-if="transaction.status === 'COMPLETED'"
                        class="dropdown-item refund-transaction"
                        @click="refundTransaction(transaction.id)"
                      >
                        <i class="fas fa-undo mr-2" />
                        {{ t('admin.transactions.action_refund') }}
                      </button>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination-section">
        <div class="pagination-info">
          <span>
            {{ t('admin.transactions.pagination_showing', {
              start: (transactionsPagination.page - 1) * transactionsPagination.limit + 1,
              end: Math.min(transactionsPagination.page * transactionsPagination.limit, transactionsPagination.total),
              total: transactionsPagination.total
            }) }}
          </span>
        </div>

        <div class="pagination-controls">
          <button
            class="pagination-btn"
            :disabled="transactionsPagination.page === 1"
            @click="changePage(transactionsPagination.page - 1)"
          >
            <i class="fas fa-chevron-left" />
          </button>

          <div class="pagination-pages">
            <button
              v-for="page in visiblePages"
              :key="page"
              class="pagination-page"
              :class="{ active: page === transactionsPagination.page }"
              @click="changePage(page)"
            >
              {{ page }}
            </button>
          </div>

          <button
            class="pagination-btn"
            :disabled="transactionsPagination.page === transactionsPagination.pages"
            @click="changePage(transactionsPagination.page + 1)"
          >
            <i class="fas fa-chevron-right" />
          </button>
        </div>
      </div>
    </div>

    <!-- Transaction Detail Modal -->
    <TransactionDetailModal
      :is-open="showDetailModal"
      :transaction="selectedTransaction"
      @close="closeDetailModal"
      @edit="handleEditFromDetail"
      @approve="handleApprove"
      @reject="handleReject"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAdminStore } from '@/stores/admin'
import type { Transaction } from '@/services/api/admin'
import TransactionDetailModal from '@/components/admin/TransactionDetailModal.vue'
import { logger } from '@/utils/logger'
import { createPaginationDisplay } from '@/utils/commonUtils'

const { t } = useI18n()

const adminStore = useAdminStore()

const paginationDisplay = createPaginationDisplay()

// State
const searchQuery = ref('')
const typeFilter = ref('')
const statusFilter = ref('')
const paymentMethodFilter = ref('')
const sortBy = ref('createdAt')
const sortOrder = ref<'asc' | 'desc'>('desc')

// Modal states
const showDetailModal = ref(false)
const selectedTransaction = ref<Transaction | null>(null)
const openMenuTransactionId = ref<string | null>(null)
const menuCloseTimeout = ref<NodeJS.Timeout | null>(null)

// Computed
const transactions = computed(() => adminStore.transactions)
const transactionsPagination = computed(() => adminStore.transactionsPagination)
const isTransactionsLoading = computed(() => adminStore.isTransactionsLoading)

const visiblePages = computed(() => {
  return paginationDisplay.getVisiblePages(transactionsPagination.value.pages, transactionsPagination.value.page, 6)
})

// Methods
const loadTransactions = async () => {
  try {
    await adminStore.fetchTransactions({
      page: transactionsPagination.value.page,
      limit: transactionsPagination.value.limit,
      search: searchQuery.value,
      type: typeFilter.value,
      status: statusFilter.value,
      paymentMethod: paymentMethodFilter.value,
      sortBy: sortBy.value,
      sortOrder: sortOrder.value,
    })
  } catch (error) {
    console.error('Failed to load transactions:', error)
  }
}

const handleSearch = debounce(() => {
  loadTransactions()
}, 500)

const changePage = (page: number) => {
  if (page >= 1 && page <= transactionsPagination.value.pages) {
    transactionsPagination.value.page = page
    loadTransactions()
  }
}

const viewTransaction = (transaction: Transaction) => {
  selectedTransaction.value = transaction
  showDetailModal.value = true
}

const handleEditFromDetail = (transaction: Transaction) => {
  // TODO: Implement edit transaction
  logger.debug('Edit transaction:', transaction)
}

const closeDetailModal = () => {
  showDetailModal.value = false
  selectedTransaction.value = null
}

const approveTransaction = async (transactionId: string) => {
  try {
    // TODO: Implement approve transaction API call
    logger.debug('Approving transaction:', transactionId)
    await loadTransactions() // Reload to get updated data
  } catch (error) {
    console.error('Failed to approve transaction:', error)
  }
}

const rejectTransaction = async (transactionId: string) => {
  try {
    // TODO: Implement reject transaction API call
    logger.debug('Rejecting transaction:', transactionId)
    await loadTransactions() // Reload to get updated data
  } catch (error) {
    console.error('Failed to reject transaction:', error)
  }
}

const refundTransaction = async (transactionId: string) => {
  try {
    // TODO: Implement refund transaction API call
    logger.debug('Refunding transaction:', transactionId)
    await loadTransactions() // Reload to get updated data
  } catch (error) {
    console.error('Failed to refund transaction:', error)
  }
}

const handleApprove = (transactionId: string) => {
  approveTransaction(transactionId)
  closeDetailModal()
}

const handleReject = (transactionId: string) => {
  rejectTransaction(transactionId)
  closeDetailModal()
}

const exportTransactions = () => {
  try {
    // Create CSV data
    const csvData = transactions.value.map(transaction => ({
      [t('admin.transactions.csv_id')]: transaction.id,
      [t('admin.transactions.csv_amount')]: `${transaction.amount.toLocaleString()} ${transaction.currency}`,
      [t('admin.transactions.csv_status')]: getStatusText(transaction.status),
      [t('admin.transactions.csv_type')]: getTypeText(transaction.type),
      [t('admin.transactions.csv_payment_method')]: transaction.paymentMethod ? getPaymentMethodText(transaction.paymentMethod) : t('admin.transactions.unknown_payment'),
      [t('admin.transactions.csv_reference')]: transaction.paymentReference || t('admin.transactions.not_set'),
      [t('admin.transactions.csv_user')]: transaction.user?.name || transaction.user?.email || t('admin.transactions.unknown_payment'),
      [t('admin.transactions.csv_email')]: transaction.user?.email || t('admin.transactions.not_set'),
      [t('admin.transactions.csv_event')]: transaction.event?.title || t('admin.transactions.no_event'),
      [t('admin.transactions.csv_created')]: formatDate(transaction.createdAt),
      [t('admin.transactions.csv_updated')]: formatDate(transaction.updatedAt)
    }))

    // Convert to CSV
    const headers = Object.keys(csvData[0] || {})
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => headers.map(header => `"${(row as any)[header] || ''}"`).join(','))
    ].join('\n')

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `transactions_${new Date().toISOString().slice(0, 10)}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } catch (error) {
    console.error('Failed to export transactions:', error)
  }
}

const getTypeText = (type: string) => {
  switch (type) {
    case 'PURCHASE': return t('admin.transactions.type_purchase')
    case 'REFUND': return t('admin.transactions.type_refund')
    case 'TRANSFER': return t('admin.transactions.type_transfer')
    case 'WITHDRAWAL': return t('admin.transactions.type_withdrawal')
    case 'DEPOSIT': return t('admin.transactions.type_deposit')
    default: return type
  }
}

const getStatusText = (status: string) => {
  switch (status) {
    case 'PENDING': return t('admin.transactions.status_pending_processing')
    case 'COMPLETED': return t('admin.transactions.status_completed')
    case 'FAILED': return t('admin.transactions.status_failed')
    case 'CANCELLED': return t('admin.transactions.status_cancelled')
    case 'REFUNDED': return t('admin.transactions.status_refunded')
    default: return status
  }
}

const getPaymentMethodText = (method: string) => {
  switch (method) {
    case 'VNPAY': return t('admin.transactions.method_vnpay')
    case 'BANK_TRANSFER': return t('admin.transactions.method_bank_transfer')
    case 'CRYPTO': return t('admin.transactions.method_crypto')
    case 'CASH': return t('admin.transactions.method_cash')
    default: return method || t('admin.transactions.not_set')
  }
}

const formatPrice = (price: number) => {
  return new Intl.NumberFormat('vi-VN').format(price)
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

const formatTime = (dateString: string) => {
  return new Date(dateString).toLocaleTimeString('vi-VN', {
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Debounce function
function debounce(func: Function, wait: number) {
  let timeout: NodeJS.Timeout
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

const toggleMenu = (transactionId: string) => {
  openMenuTransactionId.value = openMenuTransactionId.value === transactionId ? null : transactionId
}

const keepMenuOpen = (transactionId: string) => {
  if (menuCloseTimeout.value) {
    clearTimeout(menuCloseTimeout.value)
    menuCloseTimeout.value = null
  }
}

const scheduleMenuClose = (transactionId: string) => {
  if (menuCloseTimeout.value) {
    clearTimeout(menuCloseTimeout.value)
  }
  menuCloseTimeout.value = setTimeout(() => {
    if (openMenuTransactionId.value === transactionId) {
      openMenuTransactionId.value = null
    }
  }, 300)
}

const closeMenus = () => {
  openMenuTransactionId.value = null
  if (menuCloseTimeout.value) {
    clearTimeout(menuCloseTimeout.value)
    menuCloseTimeout.value = null
  }
}

// Lifecycle
onMounted(() => {
  loadTransactions()
  window.addEventListener('click', closeMenus)
})

onBeforeUnmount(() => {
  window.removeEventListener('click', closeMenus)
})

// Watchers
watch([typeFilter, statusFilter, paymentMethodFilter, sortBy, sortOrder], () => {
  loadTransactions()
})
</script>

<style scoped>
.admin-transactions-page {
  @apply px-6 py-8;
}

.page-header {
  @apply mb-8;
}

.page-title {
  @apply text-3xl font-bold text-white mb-4;
}

.page-subtitle {
  @apply text-gray-400;
}

.filters-section {
  @apply bg-white/5 border border-white/10 rounded-lg backdrop-blur-sm p-6 mb-6;
}

.filters-grid {
  @apply grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4;
}

.filter-group {
  @apply space-y-2;
}

.filter-label {
  @apply block text-sm font-medium text-gray-300;
}

.filter-input,
.filter-select {
  @apply w-full px-3 py-2 bg-white/10 border border-white/20 rounded-md;
}

.filter-select {
  @apply py-2.5 bg-adminBox border-adminBox-border;
}

.table-section {
  @apply bg-white/5 border border-white/10 rounded-lg;
}

.table-header {
  @apply flex justify-between items-center p-6 border-b border-white/15;
}

.table-title {
  @apply text-xl font-semibold text-white;
}

.table-actions {
  @apply space-x-2;
}

.table-container {
  @apply overflow-x-auto;
}

.transactions-table {
  @apply w-full;
}

.transactions-table th {
  @apply px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider bg-[#070c19] border-b border-white/15;
}

.transactions-table td {
  @apply px-6 py-4 whitespace-nowrap border-b border-white/15;
}

.loading-cell,
.empty-cell {
  @apply text-center py-12;
}

.loading-spinner {
  @apply inline-block w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mr-2;
}

.transaction-cell {
  @apply min-w-0;
}

.transaction-info {
  @apply space-y-1;
}

.transaction-id {
  @apply space-y-1;
}

.id-label {
  @apply text-xs text-gray-300 mr-1;
}

.id-value {
  @apply text-sm font-mono text-gray-400;
}

.transaction-method {
  @apply mt-1;
}

.method-badge {
  @apply inline-block px-2 py-1 bg-gray-100 text-gray-900 rounded-full text-xs font-medium;
}

.user-cell {
  @apply min-w-0;
}

.user-info {
  @apply flex items-center gap-x-3;
}

.user-avatar {
  @apply flex-shrink-0;
}

.avatar-img {
  @apply w-10 h-10 rounded-full object-cover;
}

.avatar-letter {
  @apply w-10 h-10 rounded-full bg-gray-700 text-xl flex justify-center items-center;
}

.user-details {
  @apply min-w-0 flex-1;
}

.user-name {
  @apply text-sm font-medium text-white truncate;
}

.user-email {
  @apply text-xs text-gray-400 truncate;
}

.event-cell {
  @apply min-w-0;
}

.event-info {
  @apply space-y-1;
}

.event-title {
  @apply text-sm font-medium text-white truncate;
}

.event-id {
  @apply text-xs text-gray-400;
}

.no-event-text {
  @apply text-sm text-gray-400 italic;
}

.type-cell,
.status-cell {
  @apply text-sm;
}

.type-badge,
.status-badge {
  @apply inline-flex items-center px-2.5 py-1.5 rounded-full text-xs font-medium;
}

.type-purchase {
  @apply bg-green-100 text-green-800;
}

.type-refund {
  @apply bg-red-100 text-red-800;
}

.type-transfer {
  @apply bg-blue-100 text-blue-800;
}

.type-withdrawal {
  @apply bg-orange-100 text-orange-800;
}

.type-deposit {
  @apply bg-purple-100 text-purple-800;
}

.status-pending {
  @apply bg-yellow-100 text-yellow-800;
}

.status-completed {
  @apply bg-green-100 text-green-800;
}

.status-failed {
  @apply bg-red-100 text-red-800;
}

.status-cancelled {
  @apply bg-gray-100 text-gray-800;
}

.status-refunded {
  @apply bg-blue-100 text-blue-800;
}

.amount-cell {
  @apply text-sm text-white;
}

.amount-info {
  @apply space-y-1;
}

.amount-value {
  @apply block text-lg font-bold;
}

.amount-currency {
  @apply block text-xs text-gray-400;
}

.date-cell {
  @apply text-sm text-gray-300;
}

.date-info {
  @apply space-y-1;
}

.date-text {
  @apply block font-medium;
}

.time-text {
  @apply block text-xs text-gray-400;
}

.action-buttons {
  @apply flex items-center gap-x-2;
}

.action-btn {
  @apply w-9 h-9 flex items-center justify-center rounded-lg bg-white/10 border border-white/20 hover:bg-gray-100;
}

.btn-view {
  @apply text-sm text-gray-400 hover:text-blue-600;
}

.btn-more {
  @apply text-gray-400 hover:text-gray-600;
}

.dropdown {
  @apply relative;
}

.dropdown-menu {
  @apply absolute right-0 mt-2 w-48 bg-[#070D1D] border border-gray-700 z-10;
}

.dropdown-item {
  @apply block w-full text-left px-4 py-2 text-sm text-gray-400 hover:bg-[#182235];
}

.approve-transaction {
  @apply text-green-500 hover:text-green-300;
}

.reject-transaction {
  @apply text-red-500 hover:text-red-300;
}

.refund-transaction {
  @apply text-yellow-500 hover:text-yellow-300;
}

.pagination-section {
  @apply flex justify-between items-center p-6;
}

.pagination-info {
  @apply text-sm text-gray-400;
}

.pagination-controls {
  @apply flex items-center space-x-2;
}

.pagination-btn {
  @apply w-8 h-8 flex items-center justify-center rounded-md border border-white/20 bg-white/10 text-gray-300 hover:text-white hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed;
}

.pagination-pages {
  @apply flex items-center space-x-1;
}

.pagination-page {
  @apply w-8 h-8 flex items-center justify-center rounded-md border border-white/20 bg-white/10 text-gray-300 hover:bg-white/20;
}

.pagination-page.active {
  @apply bg-blue-600 text-white border-blue-600;
}

.btn {
  @apply px-4 py-2 rounded-md font-medium;
}
</style>
